// Who may do what on a call that can have a colleague added to it (a 3-way
// call). The agent holding the call can transfer it or add one colleague; a
// colleague who was added can only leave. Leaving as the holder, with a
// colleague on the call, hands the caller to them.

import type { AgentCall, IncomingCallContext } from '@stores/voiceAgentStore';

/** A colleague added to the call, as the server reports them. */
export interface CallParticipant {
  agentId: string;
  agentName?: string;
  attemptId: string;
  state: 'ringing' | 'joined';
}

export interface CallControls {
  /** The Transfer button. */
  canTransfer: boolean;
  /** The Merge / Add colleague button. */
  canAddColleague: boolean;
  /** What the red button says. */
  leaveLabel: string;
  /** False while a call this agent placed is ringing: it can't be cancelled. */
  canEnd: boolean;
  /** The colleague who keeps the caller if this agent leaves, if any. */
  keptBy?: CallParticipant;
}

export function callControls(state: {
  activeCall: AgentCall | null;
  callContext: IncomingCallContext | null;
  participants: CallParticipant[];
  isGuest: boolean;
  outgoingCall?: { answeredAt?: string } | null;
}): CallControls {
  const { activeCall, callContext, participants, isGuest, outgoingCall } = state;
  const onCall = !!activeCall && !!callContext?.sessionId;
  const alone = participants.length === 0;
  const keptBy = isGuest ? undefined : participants.find((p) => p.state === 'joined');
  return {
    canTransfer: onCall && !!activeCall?.transferable && !isGuest && alone,
    canAddColleague: onCall && !!activeCall?.conferenceable && !isGuest && alone,
    leaveLabel: isGuest
      ? 'Leave call'
      : keptBy
        ? `Leave – ${keptBy.agentName || 'your colleague'} keeps the caller`
        : 'End call',
    canEnd: !outgoingCall || !!outgoingCall.answeredAt,
    keptBy,
  };
}

/** A colleague's name for display. */
export function participantName(participant: CallParticipant): string {
  return participant.agentName || 'Your colleague';
}
